import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MedicalRecordVisitReasonSaveOneService} from '../../service/medicalRecordVisitReasonSaveOne.service';
import {MedicalRecordVisitReasonModel} from '../../../api/model/medicalRecordVisitReason.model';
import {MomentAdapter} from '../../../../../../../shared/utils/moment.utils';
import {ConsultationSummaryModel} from '../../../../../consultation/consultation/api/model/consultationSummary.model';
import {IDialogComponent} from '../../../../../../common/dialog/iDialog.component';
import {DialogWithInput} from '../../../../../../common/dialog/dialogWithInput';
import {Moment} from 'moment';
import {
    CreateUpdateDefaultComponent
} from '../../../../../../core/component/create-update-default/create-update-default.component';
import {MedicalRecordVisitReasonFindOneService} from '../../service/medicalRecordVisitReasonFindOne .service';
import {
    ConsultationTranscriptionHandler
} from "../../../../../consultation/consultation/api/service/transcription/consultationTranscriptionHandler";
import {
    AudioTranscriptionConfig
} from "../../../../../../common/audio/transcription/api/component/audio-transcription-v2/audio-transcription-v2.component";

export interface MedicalRecordVisitReasonSaveDialogInput {
    id?: number;

    // The 2 following fields are for creation purpose only.
    consultationSummary?: ConsultationSummaryModel;
    partnerId?: number;
    startTranscription?: boolean;
}

export interface MedicalRecordVisitReasonSaveDialogResult {
    created: boolean;
    entity: MedicalRecordVisitReasonModel;
}

@Component({
    templateUrl: './medical-record-visit-reason-create-update.page.html',
    providers: [
        MedicalRecordVisitReasonSaveOneService,
        MedicalRecordVisitReasonFindOneService,
        ConsultationTranscriptionHandler
    ]
})
export class MedicalRecordVisitReasonCreateUpdatePage implements
    IDialogComponent<MedicalRecordVisitReasonSaveDialogInput, MedicalRecordVisitReasonSaveDialogResult>,
    OnInit {

    id: number;
    dialog: DialogWithInput<MedicalRecordVisitReasonSaveDialogInput, MedicalRecordVisitReasonSaveDialogResult>;
    formGroup: UntypedFormGroup;
    audioTranscriptionConfig: AudioTranscriptionConfig;

    @ViewChild('createUpdateDefaultComponent') createUpdateDefaultComponent: CreateUpdateDefaultComponent<any, any, any>;

    constructor(public service: MedicalRecordVisitReasonSaveOneService,
                public findOneService: MedicalRecordVisitReasonFindOneService,
                private formBuilder: UntypedFormBuilder,
                public transcriptionHandler: ConsultationTranscriptionHandler) {
        this.formGroup = this.formBuilder.group({
            id: new UntypedFormControl(undefined),
            comment: new UntypedFormControl(undefined),
            painIds: new UntypedFormControl([]),
            problemIds: new UntypedFormControl([]),
            labelNames: new UntypedFormControl([]),
            partnerId: new FormControl(undefined, Validators.required),
            consultationId: new FormControl(undefined),
        });
    }

    ngOnInit() {
        if (this.dialog.input.id) {
            this.id = this.dialog.input.id;
        } else {
            let context;
            if (this.dialog.input.consultationSummary) {
                context = 'consultationId=' + this.dialog.input.consultationSummary.id;
                this.consultationIdControl.setValue(this.dialog.input.consultationSummary.id);
                this.partnerIdControl.setValue(this.dialog.input.consultationSummary.partner.id);
            } else if (this.dialog.input.partnerId) {
                context = 'partnerId=' + this.dialog.input.partnerId;
                this.partnerIdControl.setValue(this.dialog.input.partnerId);
                this.synchronizeDateControl(MomentAdapter.current().get());
            } else {
                throw new Error('Invalid parameters provided');
            }


            this.transcriptionHandler.setup({
                consultationId: this.dialog.input.consultationSummary?.id,
                partnerId: this.dialog.input.partnerId,
            });
            this.audioTranscriptionConfig = {
                context,
                audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_VISIT_REASON',
                startAutomatically: this.dialog.input.startTranscription,
                enableConversationOption: false,
                handler: this.transcriptionHandler,
                showPreview: true,
            };
        }
    }

    synchronizeDateControl(date: Moment) {
        if (!this.consultationIdControl.value) {
            this.formGroup.removeControl('date');
            this.formGroup.addControl('date',
                new FormControl(date, Validators.required));
        }
    }

    get partnerId(): number {
        return this.partnerIdControl.value;
    }

    get partnerIdControl(): FormControl {
        return this.formGroup.get('partnerId') as FormControl;
    }

    get consultationIdControl(): FormControl {
        return this.formGroup.get('consultationId') as FormControl;
    }

    entityLoaded(entity: MedicalRecordVisitReasonModel) {
        this.formGroup.patchValue({
            id: entity.id,
            partnerId: entity.partner.id,
            comment: entity.comment,
            painIds: entity.pains.map(pain => pain.id),
            problemIds: entity.problems.map(problem => problem.id),
            labelNames: entity.labels.map(label => label.lovValue ? label.lovValue.label : label.customValue),
            consultationId: entity.consultation?.id
        });
        this.synchronizeDateControl(entity.date);

        let context;
        if (entity?.consultation) {
            context = 'consultationId=' + entity?.consultation.id;
        } else  {
            context = 'partnerId=' + entity?.partner?.id;
        }

        this.transcriptionHandler.setup({
            consultationId: entity?.consultation?.id,
            partnerId: entity?.partner?.id,
        });
        this.audioTranscriptionConfig = {
            context,
            audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_VISIT_REASON',
            startAutomatically: this.dialog.input.startTranscription,
            enableConversationOption: false,
            handler: this.transcriptionHandler,
            showPreview: true,
        };
    }

    entitySaved(entity: MedicalRecordVisitReasonModel) {
        this.dialog.dialog.close({
            created: !!this.formGroup.get('id').value,
            entity
        });
    }

    executeSave() {
        this.createUpdateDefaultComponent.save();
    }
}
